/**
 * Simple base component
 */

export default class BaseComponent {

    mounted = false;
    cache = [];
    restore = true;
    elm = null;
    target = null;
    _state = {};

    constructor(element="", options={}) {
        this._options = options || {};
        if (element) {
            this.elm = element;
        }
    }

    get options() {
        return this._options;
    }

    set options(defaults) {
        this._options = {
            ...this._options,
            ...defaults
        };
        return this._options;
    }

    // Create an instance of the component and associate it with a DOM node
    mount() {}

    // Destroy the component instance and do any garbage collection to release memory
    unmount() {
        this.elm = null;
    }

    // Triggered when the DOM mutates (e.g. new HTML injected)
    update() {}

    remount(element) {
        this.unmount();
        if (element) {
            this.elm = element;
        } else {
            this.elm = null;
        }
        //this.restore = true;
        this.restoreFromCache();
        this.mount();
    }

    restoreFromCache() {
        if (this.restore === true) {
            for (let key in this.cache) {
                if (this.cache.hasOwnProperty(key)) {
                    let el = document.querySelector(key);
                    if (el) {
                        el.innerHTML = this.cache[key];
                    }
                    el = null;
                }
            }
            // remove reference to allow gc to reclaim memory
            this.cache.length = 0;
            this.cache = [];
        }
    }

    get state() {
        console.warn('BaseComponent: you should not get state manually. Use getState() instead.');
        return this._state;
    }

    set state(state) {
        console.warn('BaseComponent: you should not change state manually. Use setState() instead.');
        this._state = state;
    }

    setState(scope = "local", changes) {
        let stateChanges = {};
        let stateRef = this._state;
        if (scope === 'global') {
            stateRef = BaseComponent._globalState;
        } else if (scope === 'component') {
            if (!BaseComponent._globalState.hasOwnProperty(this.constructor.name)) {
                BaseComponent._globalState[this.constructor.name] = {};
            }
            stateRef = BaseComponent._globalState[this.constructor.name];
        }

        Object.keys(changes).forEach(key => {
            if (Array.isArray(changes[key])) {
                if (stateRef[key] != null && Array.isArray(stateRef[key])) {
                    if (stateRef[key].length === changes[key].length) {
                        changes[key].some((item, index) => {
                            if (stateRef[key][index] !== item) {
                                stateChanges[key] = changes[key];
                                stateRef[key] = stateChanges[key];
                                return true;
                            }
                            return false;
                        });
                    } else {
                        stateChanges[key] = changes[key];
                        stateRef[key] = stateChanges[key];
                    }
                } else {
                    stateChanges[key] = changes[key];
                    stateRef[key] = stateChanges[key];
                }
            } else if (typeof changes[key] === 'object') {
                if (stateRef[key] != null && typeof stateRef[key] === 'object') {
                    stateChanges[key] = {};
                    Object.keys(changes[key]).forEach(subkey => {
                        if (stateRef[key][subkey] !== changes[key][subkey]) {
                            stateChanges[key][subkey] = changes[key][subkey];
                        }
                    });
                } else {
                    stateChanges[key] = changes[key];
                }

                stateRef[key] = {
                    ...stateRef[key],
                    ...stateChanges[key],
                };
            } else {
                if (stateRef !== changes[key]) {
                    stateChanges[key] = changes[key];
                    stateRef[key] = changes[key];
                }
            }
        });

        Object.keys(stateChanges).forEach(key => {
            if (Array.isArray(changes[key])) {
                if (stateChanges[key].length === 0) {
                    delete stateChanges[key];
                }
            } else if (typeof changes[key] === 'object') {
                if (Object.keys(stateChanges[key]).length === 0) {
                    delete stateChanges[key];
                }
            }
        });

        stateRef = null;
        this.stateChange(stateChanges);
    }

    stateChange(changes) {
        // this is here only to be rewritten
    }

    getState(scope = "local", defaults = {}) {
        let stateRef = this._state;
        if (scope === 'global') {
            stateRef = BaseComponent._globalState;
        } else if (scope === 'component') {
            if (BaseComponent._globalState.hasOwnProperty(this.constructor.name)) {
                stateRef = BaseComponent._globalState[this.constructor.name];
            } else {
                stateRef = {};
            }
        }
        return {
            ...defaults,
            ...stateRef
        }
    }

    destroyState(scope = "local") {
        if (scope === 'global') {
            BaseComponent._globalState = {};
        } else if (scope === 'component') {
            if (BaseComponent._globalState.hasOwnProperty(this.constructor.name)) {
                BaseComponent._globalState[this.constructor.name] = {};
            }
        } else {
            this._state = {};
        }
    }

    css(urls) {
        return Promise.all(urls.map(this._loadCSS));
    }

    _loadCSS(href) {
        return new Promise(resolve => {
            if (BaseComponent._sheets.includes(href)) {
                return resolve();
            } else {
                BaseComponent._sheets.push(href);
            }
            let link = document.createElement("link");
            link.type = "text/css";
            link.rel = "stylesheet";
            link.onload = resolve;
            link.setAttribute("href", href);
            document.head.appendChild(link);
        });
    }

    js(urls) {
        return Promise.all(urls.map(this._loadJS));
    }

    _loadJS(src) {
        return new Promise(resolve => {
            let loaded = BaseComponent.prototype.getState('component', {
                scripts: [],
            });
            if (loaded.scripts.includes(src)) {
                return resolve(false);
            } else {
                loaded.scripts.push(src);
                BaseComponent.prototype.setState('component', {
                    scripts: loaded.scripts
                });
            }
            let script = document.createElement("script");
            script.onload = resolve;
            script.setAttribute("src", src);
            document.head.appendChild(script);
        });
    }
}

Object.defineProperty(BaseComponent, '_sheets', {
    value: [],
    writable: true
});

Object.defineProperty(BaseComponent, '_globalState', {
    value: {},
    writable: true
});